import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../config";
import { SEASON_EDITION_LABEL } from "../utils/jeedigunta";
import { FaTrophy, FaArrowRight } from "react-icons/fa";

const Jeedigunta = () => {
  const [seasons, setSeasons] = useState([]);
  const [resultCounts, setResultCounts] = useState({}); // season_id → count
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: r }] = await Promise.all([
        supabase.from("jeedigunta_seasons")
          .select("season_id, name, slug, status, date_from, date_to")
          .order("season_id", { ascending: false }),
        supabase.from("jeedigunta_results")
          .select("season_id"),
      ]);
      setSeasons(s || []);
      const counts = {};
      (r || []).forEach((row) => {
        counts[row.season_id] = (counts[row.season_id] || 0) + 1;
      });
      setResultCounts(counts);
      setLoading(false);
    })();
  }, []);

  const seasonsToShow = useMemo(
    () => (seasons || []).filter((s) => s.slug),
    [seasons]
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-kk-ink via-kk-maroon-deep to-kk-ink text-kk-cream">
      <div className="max-w-5xl mx-auto px-4 py-10 md:py-16">
        {/* Header */}
        <header className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-3 mb-4">
            <FaTrophy className="text-kk-gold text-3xl md:text-4xl" />
            <h1 className="text-3xl md:text-5xl font-bold text-kk-gold-light tracking-tight">
              జీడిగుంట కథాకళ పురస్కారాలు
            </h1>
          </div>
          <p className="text-lg md:text-xl text-kk-cream/85 italic font-serif">
            Jeedigunta Kathakala Puraskaralu
          </p>
          <p className="mt-4 text-sm md:text-base text-kk-cream/70 max-w-2xl mx-auto">
            Honouring excellence in Telugu storytelling — recognised by jury,
            peers, and audience across each annual cycle.
          </p>
        </header>

        {/* Seasons */}
        <section>
          <h2 className="text-xl md:text-2xl font-semibold text-kk-gold mb-5">
            Browse by Season
          </h2>

          {loading ? (
            <p className="text-kk-cream/60">Loading…</p>
          ) : seasonsToShow.length === 0 ? (
            <p className="text-kk-cream/60">No seasons yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {seasonsToShow.map((s) => {
                const ed = SEASON_EDITION_LABEL[s.slug] || { te: "", en: "" };
                const count = resultCounts[s.season_id] || 0;
                return (
                  <Link
                    key={s.season_id}
                    to={`/jeedigunta/${s.slug}`}
                    className="group block p-6 bg-kk-ink/60 border border-kk-gold/20 rounded-2xl hover:border-kk-gold/60 hover:shadow-[0_0_24px_rgba(212,168,67,0.18)] transition-all"
                  >
                    <div className="flex items-baseline justify-between mb-3">
                      <span className="text-4xl md:text-5xl font-bold text-kk-gold-light">
                        {s.slug}
                      </span>
                      <FaArrowRight className="text-kk-gold/60 group-hover:text-kk-gold group-hover:translate-x-1 transition-all" />
                    </div>
                    <div className="text-base md:text-lg text-kk-cream font-medium">
                      {ed.te}
                    </div>
                    <div className="text-sm md:text-base text-kk-cream/70 italic mb-3">
                      {ed.en}
                    </div>
                    <div className="text-xs md:text-sm text-kk-gold/70">
                      {count > 0 ? `${count} honorees` : "Winners list pending"}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Footer / nav */}
        <footer className="mt-16 pt-8 border-t border-kk-gold/15 text-center text-sm text-kk-cream/60">
          <Link to="/" className="hover:text-kk-gold underline-offset-4 hover:underline">
            ← Back to Kathakala
          </Link>
          <span className="mx-3">·</span>
          <Link to="/utsavam" className="hover:text-kk-gold underline-offset-4 hover:underline">
            Utsavam Sessions
          </Link>
        </footer>
      </div>
    </div>
  );
};

export default Jeedigunta;
